import React, { memo, useEffect } from 'react';
import { Col, Form, Input, InputNumber, Row, Space, Typography } from 'antd';
import styled from 'styled-components';
import { CloseOutlined, PlusCircleFilled } from '@ant-design/icons';
import { COLORS, TEXT_SIZES } from '../../../utils/constants';
import { createMultipleCourtsRequest } from '../../../api/requests/sports';
import { CreateMultipleCourtType } from '../../../types/types';
import Button from '../../common/Button';
import Modal from '../../common/Modal';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import { handleNumericKeyDown, parseNumericInput } from '../../../utils/utils';

interface CourtFormValue {
  courtId: number;
  courtName: string;
  weekdayPrice?: number;
  weekendPrice?: number;
}

interface CourtFormValues {
  sportId: number;
  courts: CourtFormValue[];
}

interface CourtFormModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: () => void;
  facilitySportId: number;
  sportName: string;
}

const CreateCourtModal: React.FC<CourtFormModalProps> = ({
  visible,
  onClose,
  onSubmit,
  facilitySportId,
  sportName,
}) => {
  const [form] = Form.useForm<CourtFormValues>();
  const snackbar = useSnackbar();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (values) {
        await createCourt(values);
      }
    } catch (error) {
      console.warn('Validation failed:', error);
    }
  };

  const createCourt = async (values: CourtFormValues) => {
    try {
      const formattedValues: CreateMultipleCourtType = {
        ...values,
        courtNames: values.courts.map((court) => {
          return {
            courtName: court.courtName,
            regularHourlyPrice: court.weekdayPrice,
            weekendHourlyPrice: court.weekendPrice,
          };
        }),
        facilitySportId: facilitySportId,
      };
      const response = await createMultipleCourtsRequest(formattedValues);
      if (response?.isSuccess) {
        onSubmit();
        form.resetFields();
        snackbar.success('Courts created successfully');
      } else {
        snackbar.error(response?.error);
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Error creating courts');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    form.setFieldsValue({
      courts: [
        {
          courtName: '',
        },
      ],
    });
  }, [form]);

  return (
    <Modal
      title={'Add New Court'}
      open={visible}
      onCancel={handleCancel}
      minHeight={'auto'}
    >
      <Form form={form} layout="vertical">
        <Form.List name="courts">
          {(fields, { add, remove }) => (
            <CourtsContainer>
              {fields.map((field, idx) => {
                return (
                  <CourtContainer key={field.key}>
                    <Row justify="space-between">
                      <CourtLabel>
                        <MandatorySymbol>*</MandatorySymbol> Court Name
                      </CourtLabel>
                      {fields.length > 1 && (
                        <StyledCloseOutlined
                          onClick={() => {
                            remove(field.name);
                          }}
                        />
                      )}
                    </Row>
                    <StyledCourtSpace align="center">
                      <Col>
                        <Row>
                          <CourtFormItem
                            {...field}
                            name={[field.name, 'courtName']}
                            rules={[
                              {
                                required: true,
                                message: 'Please enter court name',
                              },
                            ]}
                          >
                            <StyledInput
                              placeholder="Enter court name"
                              size="large"
                            />
                          </CourtFormItem>
                        </Row>
                        <Row justify="space-between" gutter={16}>
                          <Col span={12}>
                            <CourtLabel>Weekday(₹/hr)</CourtLabel>
                            <CourtFormItem name={[field.name, 'weekdayPrice']}>
                              <StyledInputNumber
                                placeholder="499"
                                size="large"
                                min="0"
                                step="1"
                                parser={parseNumericInput}
                                onKeyDown={handleNumericKeyDown}
                              />
                            </CourtFormItem>
                          </Col>
                          <Col span={12}>
                            <CourtLabel>Weekend(₹/hr)</CourtLabel>
                            <CourtFormItem name={[field.name, 'weekendPrice']}>
                              <StyledInputNumber
                                placeholder="799"
                                size="large"
                                min="0"
                                step="1"
                                parser={parseNumericInput}
                                onKeyDown={handleNumericKeyDown}
                              />
                            </CourtFormItem>
                          </Col>
                        </Row>
                      </Col>
                    </StyledCourtSpace>
                  </CourtContainer>
                );
              })}
              <AddCourtButton>
                <Button
                  onClick={() => add({ courtName: '' })}
                  icon={<StyledPlusIcon />}
                  size="large"
                  style={{ fontSize: TEXT_SIZES.MD }}
                  isBlock
                >
                  Add New Court
                </Button>
              </AddCourtButton>
            </CourtsContainer>
          )}
        </Form.List>

        <ButtonSection>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit}>
            Add Courts
          </Button>
        </ButtonSection>
      </Form>
    </Modal>
  );
};

const StyledFormItem = styled(Form.Item)`
  width: 100%;
  &.ant-space-item {
    flex: 1;
  }
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 32px;
`;

const MandatorySymbol = styled.span`
  color: red;
`;

const StyledCourtSpace = styled(Space)`
  display: flex;
  width: 100%;
  & .ant-space-item:first-child {
    flex: 1;
  }
  margin-bottom: 10px;
`;

const CourtsContainer = styled.div`
  margin-bottom: 16px;
`;

const CourtLabel = styled(Typography.Text)`
  display: block;
  margin-bottom: 8px;
`;

const CourtFormItem = styled(StyledFormItem)`
  flex: 1;
  margin-bottom: 0;
`;

const StyledCloseOutlined = styled(CloseOutlined)`
  color: ${COLORS.red.primary};
  font-size: ${TEXT_SIZES.BASE};
  cursor: pointer;
  margin-bottom: 8px;
`;

const StyledInput = styled(Input)`
  width: 100%;
  height: 40px;
  margin-bottom: 8px;

  .ant-input-group-addon {
    font-size: ${TEXT_SIZES.XS}px;
    padding: 0 8px;
    background-color: ${COLORS.grey.primary};
    color: ${COLORS.grey.secondary};
  }
`;

const StyledPlusIcon = styled(PlusCircleFilled)`
  font-size: ${TEXT_SIZES.LG}px;
`;

const AddCourtButton = styled(Form.Item)`
  margin-top: 16px;
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
`;

const CourtContainer = styled.div`
  border: 1px solid ${COLORS.grey.lightMedium};
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px ${COLORS.grey.lightMedium};
`;

export default memo(CreateCourtModal);
